import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Loader2 } from "lucide-react";
import { api, assetUrl, fmtDuration } from "@/lib/api";

const fmtDate = (iso) => new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", timeZoneName: "short" }) : "—");

export default function ReportPage() {
  const { id, leadId } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/campaigns/${id}/reports/${leadId}`).then((res) => {
      setR(res.data);
      document.title = `${res.data.template.heading} – ${res.data.lead.email}`;
    }).catch((e) => setErr(e.response?.data?.detail || "Report not found"));
  }, [id, leadId]);

  if (err) return <div className="min-h-screen grid place-items-center bg-[#0B0F17] text-slate-300">{err}</div>;
  if (!r) return <div className="min-h-screen grid place-items-center bg-[#0B0F17]"><Loader2 className="w-6 h-6 animate-spin text-indigo-400" /></div>;

  const t = r.template;
  const l = r.lead;
  const rows = [
    t.show_email && ["Email ID", l.email],
    t.show_date && ["Date", fmtDate(l.date)],
    t.show_start && ["Start time", fmtTime(l.start_time)],
    t.show_end && ["End time", fmtTime(l.end_time)],
    t.show_duration && [r.campaign_type === "whitepaper" ? "Total reading time" : "Total duration", l.watched ? fmtDuration(l.total_duration_seconds) : (r.campaign_type === "whitepaper" ? "Not read" : "Not watched")],
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0B0F17] py-10 px-4 print:bg-white print:p-0">
      <div className="max-w-3xl mx-auto mb-6 flex items-center justify-between print:hidden">
        <Link to={`/admin/campaigns/${id}`} data-testid="report-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back to campaign</Link>
        <button data-testid="report-print-button" onClick={() => window.print()}
          className="h-10 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-medium flex items-center gap-2">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>
      <div data-testid="report-page" className="max-w-3xl mx-auto bg-white text-slate-900 rounded-xl shadow-2xl print:shadow-none print:rounded-none px-10 sm:px-16 py-14">
        {t.logo_path && <img src={assetUrl(t.logo_path)} alt="Logo" className="h-12 w-auto object-contain mb-10" data-testid="report-logo" />}
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-2">{t.subtitle}</p>
        <h1 data-testid="report-heading" className="text-3xl sm:text-4xl font-bold tracking-tight mb-8">{t.heading}</h1>
        <div className="border-y border-slate-200 py-5 mb-8">
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-1">{r.campaign_type === "whitepaper" ? "White paper campaign" : "Webinar"}</p>
          <p data-testid="report-webinar-title" className="text-xl font-semibold">{r.webinar_title}</p>
        </div>
        <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-5 mb-10">
          {rows.map(([k, v]) => (
            <div key={k} data-testid={`report-field-${k.toLowerCase().replace(/\s+/g, "-")}`}>
              <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-1">{k}</dt>
              <dd className="text-base font-medium">{v}</dd>
            </div>
          ))}
        </dl>
        {r.chats?.length > 0 && (
          <div className="mb-10" data-testid="report-chats">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Questions asked to the AI assistant ({r.chats.length})</p>
            <div className="space-y-3">
              {r.chats.map((m) => (
                <div key={m.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                  <p className="font-medium">Q: {m.question}</p>
                  <p className="text-slate-600 mt-1 whitespace-pre-wrap">A: {m.answer}</p>
                  <p className="text-[10px] text-slate-400 mt-1">{new Date(m.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        {t.footer && <p data-testid="report-footer" className="text-xs text-slate-500 leading-relaxed border-t border-slate-200 pt-5 whitespace-pre-line">{t.footer}</p>}
      </div>
    </div>
  );
}
